import React from "react";
import { apiUrl } from "../lib/apiBase";
import { getAuthToken } from "../lib/auth";
import { isNativeStoreApp } from "../lib/appMode";
import { getNativePushPermission } from "../lib/nativePush";

const DEFAULTS = {
  master: true,
  live: true,
  social: true,
  tournaments: true,
  announcements: true,
  sound: true,
  badge: true,
};

const ROWS = [
  ["live", "Streamer live alerts", "Opens the exact GAStreaming channel that started streaming."],
  ["social", "Mentions and social activity", "Alerts when your exact @username is mentioned in Feed comments."],
  ["tournaments", "Tournament updates", "Registration, team, bracket, result, and champion updates."],
  ["announcements", "GAStreaming announcements", "New features, new tournaments, and important platform news."],
  ["sound", "Notification sounds", "Use the GAStreaming sound assigned to each alert type."],
  ["badge", "App icon badge", "Show an unread count on the GAStreaming app icon when supported."],
];

export default function PushNotificationSettings() {
  const [preferences, setPreferences] = React.useState(DEFAULTS);
  const [permission, setPermission] = React.useState("checking");
  const [registered, setRegistered] = React.useState(false);
  const [busyKey, setBusyKey] = React.useState("");
  const [message, setMessage] = React.useState("");

  const load = React.useCallback(async () => {
    if (!isNativeStoreApp()) return;
    const token = getAuthToken();
    if (!token) return;
    setPermission(await getNativePushPermission());
    try {
      const response = await fetch(apiUrl("/push/settings"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || data?.error || "Could not load phone settings.");
      setPreferences({ ...DEFAULTS, ...(data?.preferences || {}) });
      setRegistered(Boolean(data?.registered));
    } catch (error) {
      setMessage(error?.message || "Could not load phone settings.");
    }
  }, []);

  React.useEffect(() => {
    load();
    window.addEventListener("ga:push-settings-refresh", load);
    window.addEventListener("ga:push-registration", load);
    return () => {
      window.removeEventListener("ga:push-settings-refresh", load);
      window.removeEventListener("ga:push-registration", load);
    };
  }, [load]);

  if (!isNativeStoreApp()) return null;

  const save = async (key, value) => {
    const token = getAuthToken();
    if (!token) return;
    const previous = preferences;
    const next = { ...preferences, [key]: value };
    setPreferences(next);
    setBusyKey(key);
    setMessage("");
    try {
      const response = await fetch(apiUrl("/push/settings"), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ [key]: value }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || data?.error || "Could not save this setting.");
      setPreferences({ ...DEFAULTS, ...(data?.preferences || next) });
    } catch (error) {
      setPreferences(previous);
      setMessage(error?.message || "Could not save this setting.");
    } finally {
      setBusyKey("");
    }
  };

  const permissionLabel =
    permission === "granted"
      ? registered
        ? "Enabled on this phone"
        : "Permission granted — finishing registration"
      : permission === "denied"
      ? "Turned off in phone settings"
      : "Not enabled yet";

  return (
    <section
      id="phone-notifications"
      style={{
        marginTop: 18,
        borderRadius: 18,
        padding: 16,
        border: "1px solid rgba(255,255,255,0.12)",
        background:
          "radial-gradient(900px 460px at 15% 10%, rgba(255,0,64,0.13), transparent 60%), rgba(8,10,16,.70)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 950, fontSize: 16 }}>Phone Notifications</div>
          <div style={{ opacity: 0.8, fontSize: 13, marginTop: 3 }}>{permissionLabel}</div>
        </div>
        {permission !== "granted" || !registered ? (
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("ga:push-enable-request"))}
            style={{
              border: 0,
              borderRadius: 999,
              padding: "10px 14px",
              color: "white",
              background: "linear-gradient(135deg, #ff1744, #b00020)",
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            Enable on this phone
          </button>
        ) : null}
      </div>

      <label
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 14,
          marginTop: 15,
          padding: "12px 0",
          borderTop: "1px solid rgba(255,255,255,.10)",
          borderBottom: "1px solid rgba(255,255,255,.10)",
        }}
      >
        <span>
          <strong>All phone notifications</strong>
          <span style={{ display: "block", opacity: 0.7, fontSize: 12, marginTop: 3 }}>Master switch for this account.</span>
        </span>
        <input
          type="checkbox"
          checked={preferences.master}
          disabled={busyKey === "master"}
          onChange={(event) => save("master", event.target.checked)}
          style={{ width: 24, height: 24, accentColor: "#ff1744" }}
        />
      </label>

      <div style={{ display: "grid", gap: 0 }}>
        {ROWS.map(([key, label, detail]) => (
          <label
            key={key}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 14,
              padding: "12px 0",
              opacity: preferences.master ? 1 : 0.58,
              borderBottom: "1px solid rgba(255,255,255,.07)",
            }}
          >
            <span style={{ minWidth: 0 }}>
              <strong style={{ fontSize: 14 }}>{label}</strong>
              <span style={{ display: "block", opacity: 0.7, fontSize: 12, lineHeight: 1.35, marginTop: 3 }}>{detail}</span>
            </span>
            <input
              type="checkbox"
              checked={Boolean(preferences[key])}
              disabled={!preferences.master || busyKey === key}
              onChange={(event) => save(key, event.target.checked)}
              style={{ width: 22, height: 22, minWidth: 22, accentColor: "#ff1744" }}
            />
          </label>
        ))}
      </div>
      {message ? <div style={{ color: "#ffb7c3", fontSize: 13, marginTop: 12 }}>{message}</div> : null}
    </section>
  );
}
